import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from '../../../components/RequestDefinition';
import {Attribute} from '../../../components/ObjectAttribute';

const updateTransferOrderRequest: RequestExample = {
    title: {
        verb: 'POST',
        endpointUrl: '/api/transfer-orders/:id',
    },
    curl:
        `curl --location \\
--request POST '<YOUR SERVER>/api/transfer-orders/:id' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer <TOKEN>' \\
--data-raw '<TO JSON>'
`,
};

const updateTransferOrderResponse: Json = {
    title: 'RESPONSE',
    json: {
        'id': 14,
        'number': 'TO-00014',
        'status': 'Entered',
        'type': 'Ship',
        'fromLocationGroup': {
            'id': 1,
            'name': 'Main Warehouse'
        },
        'toLocationGroup': {
            'id': 2,
            'name': 'East Warehouse'
        },
        'carrier': {
            'id': 5,
            'name': 'USPS',
            'service': {
                'id': 3,
                'name': 'Priority Mail'
            }
        },
        'shipFromAddress': {
            'name': 'Main Warehouse',
            'street': '100 Main St',
            'city': 'Salt Lake City',
            'state': 'UT',
            'postalCode': '84101',
            'country': 'UNITED STATES'
        },
        'shipToAddress': {
            'name': 'East Warehouse',
            'street': '200 East Blvd',
            'city': 'Denver',
            'state': 'CO',
            'postalCode': '80201',
            'country': 'UNITED STATES'
        },
        'note': 'Updated via REST API',
        'ownerIsFrom': true,
        'revisionNumber': 2,
        'lastModified': {
            'username': 'admin',
            'dateLastModified': '2024-03-02T09:30:00.000-0700'
        },
        'dateCreated': '2024-03-01T08:00:00.000-0700',
        'dateScheduled': '2024-03-10T00:00:00.000-0700',
        'dateConfirmed': '2024-03-02T00:00:00.000-0700',
        'toItems': [
            {
                'id': 101,
                'part': {
                    'id': 28,
                    'name': 'B201',
                    'description': 'Premium Brake Cables'
                },
                'type': {
                    'id': 10,
                    'name': 'Send'
                },
                'status': 'Entered',
                'uom': {
                    'id': 1,
                    'name': 'Each',
                    'abbreviation': 'ea'
                },
                'lineNumber': 1,
                'note': 'Updated quantity',
                'quantity': '20',
                'quantityFulfilled': '3',
                'quantityPicked': '8',
                'unitCost': '12.50',
                'dateScheduled': '2024-03-10T00:00:00.000-0700'
            }
        ],
        'customFields': [
            {
                'id': 22,
                'name': 'Custom',
                'value': 'Updated value',
                'type': 'Text'
            }
        ]
    },
};

const updateTransferOrderAttributes: Attribute[] = [
    {
        name: 'id',
        type: 'integer',
        optional: true,
        description: 'The transfer order ID. If provided, must match the ID in the URL.',
    },
    {
        name: 'lastModified',
        type: 'object',
        description: 'Used for optimistic locking. Must match the current dateLastModified of the transfer order.',
        children: [
            {
                name: 'dateLastModified',
                type: 'date',
                description: 'The date the transfer order was last modified. Returned by GET /transfer-orders/:id.',
            },
        ],
    },
    {
        name: 'type',
        type: 'string: \'Ship\' | \'Move\'',
        optional: true,
        description: 'The transfer order type. Can only be changed when the order is in Entered status.',
    },
    {
        name: 'fromLocationGroup',
        type: 'reference object',
        optional: true,
        description: 'The source location group. Can only be changed when the order is in Entered status.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ],
    },
    {
        name: 'toLocationGroup',
        type: 'reference object',
        optional: true,
        description: 'The destination location group. Can only be changed when the order is in Entered status.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ],
    },
    {
        name: 'shipFromAddress',
        type: 'address object',
        optional: true,
        description: 'The ship-from address. Required for Ship type; ignored for Move type.',
        children: [
            { name: 'name', type: 'string', optional: true, description: 'The address name.' },
            { name: 'street', type: 'string', optional: true, description: 'The street address.' },
            { name: 'city', type: 'string', optional: true, description: 'The city.' },
            { name: 'state', type: 'string', optional: true, description: 'The state code or name.' },
            { name: 'postalCode', type: 'string', optional: true, description: 'The postal code.' },
            { name: 'country', type: 'string', optional: true, description: 'The country name or code.' },
        ],
    },
    {
        name: 'shipToAddress',
        type: 'address object',
        optional: true,
        description: 'The ship-to address. Required for Ship type; ignored for Move type.',
        children: [
            { name: 'name', type: 'string', optional: true, description: 'The address name.' },
            { name: 'street', type: 'string', optional: true, description: 'The street address.' },
            { name: 'city', type: 'string', optional: true, description: 'The city.' },
            { name: 'state', type: 'string', optional: true, description: 'The state code or name.' },
            { name: 'postalCode', type: 'string', optional: true, description: 'The postal code.' },
            { name: 'country', type: 'string', optional: true, description: 'The country name or code.' },
        ],
    },
    {
        name: 'carrier',
        type: 'reference object',
        optional: true,
        description: 'The carrier for the transfer order. Omit to leave the existing carrier unchanged.',
        children: [
            { name: 'id', type: 'integer', description: 'The unique identification number.' },
            {
                name: 'service',
                type: 'reference object',
                optional: true,
                description: 'The carrier service.',
                children: [
                    { name: 'id', type: 'integer', description: 'The carrier service ID.' },
                ],
            },
        ],
    },
    {
        name: 'note',
        type: 'string',
        optional: true,
        description: 'A note on the transfer order.',
    },
    {
        name: 'dateScheduled',
        type: 'date',
        optional: true,
        description: 'The scheduled fulfillment date.',
    },
    {
        name: 'dateConfirmed',
        type: 'date',
        optional: true,
        description: 'The date the order was confirmed.',
    },
    {
        name: 'toItems',
        type: 'list',
        description: 'The complete list of transfer order items. Items not included are deleted. At least one item is required.',
        children: [
            {
                name: 'id',
                type: 'integer',
                optional: true,
                description: 'The existing item ID. Omit or set to 0 to add a new item.',
            },
            {
                name: 'part',
                type: 'reference object',
                description: 'The part to transfer.',
                children: [
                    { name: 'id', type: 'integer', description: 'The unique identification number.' },
                ],
            },
            {
                name: 'uom',
                type: 'reference object',
                optional: true,
                description: 'The unit of measure.',
                children: [
                    { name: 'id', type: 'integer', description: 'The unique identification number.' },
                ],
            },
            {
                name: 'type',
                type: 'reference object',
                optional: true,
                description: 'The item type.',
                children: [
                    { name: 'id', type: 'integer', description: 'The unique identification number.' },
                ],
            },
            {
                name: 'quantity',
                type: 'quantity',
                description: 'The quantity to transfer. Must be greater than zero.',
            },
            {
                name: 'note',
                type: 'string',
                optional: true,
                description: 'A note on the line item.',
            },
            {
                name: 'unitCost',
                type: 'money',
                optional: true,
                description: 'The unit cost of the item. Honored only for non-inventory or shipping parts on Ship-type transfer orders; forced to zero for inventory parts and for Move-type orders — including when an order is changed from Ship to Move. On an eligible item, omitting it preserves the existing unit cost; send an explicit value to change it, or send 0 to clear it.',
            },
            {
                name: 'dateScheduled',
                type: 'date',
                optional: true,
                description: 'The scheduled fulfillment date for this item.',
            },
        ],
    },
];

const data: RequestDefinitionData = {
    id: 'update-transfer-order',
    title: 'Update a transfer order',
    description: 'Updates an existing transfer order. The toItems list is authoritative — any existing items ' +
        'not included in the request are deleted. Type and location groups can only be changed when the order ' +
        'is in Entered status. While the order is in Entered status, the owner location group — the from group ' +
        'when ownerIsFrom is true, otherwise the to group — must be one you have write access to; an update that ' +
        'would make the owner a location group you cannot access returns 403 with the message "Access denied. User ' +
        'does not have access to the owner location group." and does not save. The non-owner location group may be ' +
        'any active location group, including one you cannot access. The lastModified.dateLastModified field is ' +
        'required for optimistic locking and must match the current value returned by GET /transfer-orders/:id.',
    endpoint: {
        verb: 'POST',
        path: '/transfer-orders/:id',
        tooltip: 'Update a transfer order',
    },
    attributes: updateTransferOrderAttributes,
    requestObject: updateTransferOrderRequest,
    responseObject: updateTransferOrderResponse,
};

export default data;
